import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ReactFlow, MiniMap, Controls, Background, useNodesState, useEdgesState, Panel, NodeMouseHandler, ReactFlowProvider } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { LdgGraphNode } from './LdgGraphNode';
import { NodeDetailPanel } from './NodeDetailPanel';
import { useLdgGraphData, LdgNodeData } from './useLdgGraphData';
import { MODULES } from '../../../../content';
import { Filter, GitBranch, ChevronDown, ChevronUp, X } from 'lucide-react';
const nodeTypes = {
  ldgNode: LdgGraphNode
};
function LdgGraphExplorerInner() {
  const [activeModules, setActiveModules] = useState<Set<string>>(new Set());
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const {
    nodes: initialNodes,
    edges: initialEdges,
    stats
  } = useLdgGraphData(activeModules);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges, setNodes, setEdges]);
  const toggleModule = (moduleId: string) => {
    setActiveModules(prev => {
      const next = new Set(prev);
      if (next.has(moduleId)) {
        next.delete(moduleId);
      } else {
        next.add(moduleId);
      }
      return next;
    });
    setSelectedNodeId(null);
  };
  const onNodeClick: NodeMouseHandler = useCallback((_, node) => {
    setSelectedNodeId(node.id);
  }, []);
  const handleSelectNode = useCallback((nodeId: string) => {
    setSelectedNodeId(nodeId);
  }, []);
  const selectedNodeData = useMemo(() => {
    if (!selectedNodeId) return null;
    const node = nodes.find(n => n.id === selectedNodeId);
    return node ? node.data as LdgNodeData : null;
  }, [selectedNodeId, nodes]);
  const activeFilterCount = activeModules.size;
  return <div className="w-full h-full relative bg-slate-50 flex flex-col" data-id="element-5285">
      {/* ── Mobile: Compact top bar ── */}
      <div className="md:hidden flex-shrink-0 bg-white/95 backdrop-blur-md border-b border-slate-200 z-20" data-id="element-5286">
        {/* Stats row */}
        <div className="flex items-center justify-between px-3 py-2" data-id="element-5287">
          <div className="flex items-center gap-3" data-id="element-5288">
            <div className="flex items-center gap-1.5" data-id="element-5289">
              <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" data-id="element-5290" />
              <span className="text-xs font-bold text-slate-700" data-id="element-5291">
                {stats.nodeCount}
              </span>
              <span className="text-[10px] text-slate-400" data-id="element-5292">nodes</span>
            </div>
            <div className="flex items-center gap-1.5" data-id="element-5293">
              <div className="w-1.5 h-1.5 rounded-full bg-slate-400" data-id="element-5294" />
              <span className="text-xs font-bold text-slate-700" data-id="element-5295">
                {stats.edgeCount}
              </span>
              <span className="text-[10px] text-slate-400" data-id="element-5296">edges</span>
            </div>
            <div className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${stats.isValidDAG ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`} data-id="element-5297">
              {stats.isValidDAG ? 'DAG ✓' : 'DAG ✗'}
            </div>
          </div>
          <button onClick={() => setPanelOpen(!panelOpen)} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${panelOpen ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600'}`} data-id="element-5298">
            <Filter className="w-3.5 h-3.5" data-id="element-5299" />
            Filter
            {activeFilterCount > 0 && <span className="bg-indigo-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center" data-id="element-5300">
                {activeFilterCount}
              </span>}
            {panelOpen ? <ChevronUp className="w-3 h-3" data-id="element-5301" /> : <ChevronDown className="w-3 h-3" data-id="element-5302" />}
          </button>
        </div>

        {/* Collapsible filter chips */}
        {panelOpen && <div className="px-3 pb-3 border-t border-slate-100 pt-2" data-id="element-5303">
            <div className="flex flex-wrap gap-1.5" data-id="element-5304">
              {MODULES.map(mod => {
            const isActive = activeModules.size === 0 || activeModules.has(mod.id);
            return <button key={mod.id} onClick={() => toggleModule(mod.id)} className={`px-2 py-1 rounded-full text-[11px] font-medium transition-colors border ${isActive ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-500 border-slate-200'}`} data-id="element-5305">
                    {mod.id}
                  </button>;
          })}
            </div>
            {activeModules.size > 0 && <button onClick={() => setActiveModules(new Set())} className="mt-2 text-xs font-medium text-indigo-600 w-full text-center py-1 bg-indigo-50 rounded-lg" data-id="element-5306">
                Toon alles
              </button>}
          </div>}
      </div>

      {/* ── Desktop: Floating panels (hidden on mobile) ── */}
      <div className="hidden md:flex absolute top-4 left-4 z-10 flex-col gap-3 pointer-events-none" data-id="element-5307">
        {/* Stats Panel */}
        <div className="bg-white/90 backdrop-blur-md p-3 rounded-xl shadow-sm border border-slate-200 pointer-events-auto w-72" data-id="element-5308">
          <div className="flex items-center gap-2 mb-2.5" data-id="element-5309">
            <div className="p-1 bg-indigo-100 text-indigo-600 rounded-lg" data-id="element-5310">
              <GitBranch className="w-4 h-4" data-id="element-5311" />
            </div>
            <div data-id="element-5312">
              <h2 className="font-bold text-sm text-slate-900 leading-tight" data-id="element-5313">
                Learning Dependency Graph
              </h2>
              <p className="text-[10px] text-slate-500" data-id="element-5314">
                Canonical Taxonomy Structure
              </p>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-1.5" data-id="element-5315">
            <div className="bg-slate-50 p-1.5 rounded-lg border border-slate-100 text-center" data-id="element-5316">
              <div className="text-[9px] font-bold text-slate-400 uppercase" data-id="element-5317">
                Nodes
              </div>
              <div className="font-bold text-sm text-slate-700" data-id="element-5318">
                {stats.nodeCount}
              </div>
            </div>
            <div className="bg-slate-50 p-1.5 rounded-lg border border-slate-100 text-center" data-id="element-5319">
              <div className="text-[9px] font-bold text-slate-400 uppercase" data-id="element-5320">
                Edges
              </div>
              <div className="font-bold text-sm text-slate-700" data-id="element-5321">
                {stats.edgeCount}
              </div>
            </div>
            <div className="bg-slate-50 p-1.5 rounded-lg border border-slate-100 text-center" data-id="element-5322">
              <div className="text-[9px] font-bold text-slate-400 uppercase" data-id="element-5323">
                Depth
              </div>
              <div className="font-bold text-sm text-slate-700" data-id="element-5324">
                {stats.maxDepth}
              </div>
            </div>
            <div className="bg-slate-50 p-1.5 rounded-lg border border-slate-100 text-center" data-id="element-5325">
              <div className="text-[9px] font-bold text-slate-400 uppercase" data-id="element-5326">
                DAG
              </div>
              <div className={`font-bold text-sm ${stats.isValidDAG ? 'text-emerald-600' : 'text-rose-600'}`} data-id="element-5327">
                {stats.isValidDAG ? '✓' : '✗'}
              </div>
            </div>
          </div>
        </div>

        {/* Filter Panel */}
        <div className="bg-white/90 backdrop-blur-md p-3 rounded-xl shadow-sm border border-slate-200 pointer-events-auto w-72" data-id="element-5328">
          <div className="flex items-center gap-2 mb-2" data-id="element-5329">
            <Filter className="w-3.5 h-3.5 text-slate-400" data-id="element-5330" />
            <h3 className="font-bold text-xs text-slate-700" data-id="element-5331">
              Filter op Module
            </h3>
          </div>
          <div className="flex flex-wrap gap-1.5" data-id="element-5332">
            {MODULES.map(mod => {
            const isActive = activeModules.size === 0 || activeModules.has(mod.id);
            return <button key={mod.id} onClick={() => toggleModule(mod.id)} className={`px-2 py-0.5 rounded-full text-[11px] font-medium transition-colors border ${isActive ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300 hover:bg-slate-50'}`} data-id="element-5333">
                  {mod.id}
                </button>;
          })}
          </div>
          {activeModules.size > 0 && <button onClick={() => setActiveModules(new Set())} className="mt-2 text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors w-full text-center py-1 bg-indigo-50 rounded-lg" data-id="element-5334">
              Toon alles
            </button>}
        </div>
      </div>

      {/* ReactFlow Canvas */}
      <div className="flex-1 w-full h-full min-h-0" data-id="element-5335">
        <ReactFlow nodes={nodes} edges={edges} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onNodeClick={onNodeClick} nodeTypes={nodeTypes} fitView fitViewOptions={{
        padding: 0.2
      }} minZoom={0.05} maxZoom={1.5} defaultEdgeOptions={{
        zIndex: 0
      }} data-id="element-5336">
          <Background color="#cbd5e1" gap={20} size={1} data-id="element-5337" />
          <Controls className="bg-white border-slate-200 shadow-sm rounded-lg overflow-hidden" showInteractive={false} data-id="element-5338" />
          <MiniMap className="hidden md:block bg-white border-slate-200 shadow-sm rounded-xl overflow-hidden" nodeColor={node => {
          const data = node.data as LdgNodeData;
          return data.colorSet?.ring || '#cbd5e1';
        }} maskColor="rgba(248, 250, 252, 0.7)" data-id="element-5339" />
        </ReactFlow>
      </div>

      {/* Detail Panel */}
      <NodeDetailPanel nodeData={selectedNodeData} onClose={() => setSelectedNodeId(null)} onSelectNode={handleSelectNode} data-id="element-5340" />
    </div>;
}
export function LdgGraphExplorer() {
  return <ReactFlowProvider data-id="element-5341">
      <LdgGraphExplorerInner data-id="element-5342" />
    </ReactFlowProvider>;
}